import React, { useEffect, useState } from "react";
import { Page, Text, View, Document, StyleSheet, Image } from "@react-pdf/renderer";
import QRCode from "qrcode";

// Avery 5160 specifications in points (1 inch = 72 points)
const AVERY_5160 = {
    pageWidth: 612,     // 8.5 inches
    pageHeight: 792,    // 11 inches
    labelWidth: 189,    // 2.625 inches  
    labelHeight: 72,    // 1 inch
    topMargin: 36,      // 0.5 inches
    leftMargin: 13.5,   // 0.1875 inches
    horizontalGap: 9,   // 0.125 inches
    verticalGap: 0,     // No gap between rows
    cols: 3,
    rows: 10,
    labelsPerPage: 30   // 3 columns × 10 rows
};

// Function to generate QR code data URL
const generateQRCode = async (menuItemId) => {
    const url = `https://fmb.austinjamaat.org/feedback/${menuItemId}`;
    try {
        return await QRCode.toDataURL(url, {
            width: 28,  // Increased by 10% from 25
            margin: 0,
            color: {
                dark: '#000000',
                light: '#ffffff'
            }
        });
    } catch (err) {
        console.error('Error generating QR code:', err);
        return null;
    }
};

const styles = StyleSheet.create({
    page: {
        backgroundColor: "#ffffff",
        width: AVERY_5160.pageWidth,
        height: AVERY_5160.pageHeight,
        paddingTop: AVERY_5160.topMargin,
        paddingLeft: AVERY_5160.leftMargin,
    },
    labelContainer: {
        width: AVERY_5160.labelWidth,
        height: AVERY_5160.labelHeight,
        padding: 4,
        fontSize: 10,
        fontFamily: 'Helvetica',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        position: 'relative',  // Added for absolute positioning of QR code
    },
    qrCode: {
        width: 28,
        height: 28,
        position: 'absolute',
        left: 8,  
        bottom: 4,
    },
    labelRow: {
        flexDirection: 'row',
        gap: AVERY_5160.horizontalGap,
        marginBottom: AVERY_5160.verticalGap,
    },
    nameText: {
        fontSize: 12,
        fontFamily: 'Helvetica-Bold',
        marginBottom: 2,
        textAlign: 'center',
    },
    zoneText: {
        fontSize: 12,
        fontFamily: 'Helvetica-Bold',
        marginBottom: 3,
        textAlign: 'center',
    },
    dishText: {
        fontSize: 7,
        marginBottom: 1,
        textAlign: 'center',
    },
    dayText: {
        fontSize: 7,
        marginBottom: 1,
        textAlign: 'center',
        fontFamily: 'Helvetica-Bold',
    },
    sizeText: {
        fontSize: 7,
        textAlign: 'center',
    }
});

const AveryPdfDocument = ({ labelData }) => {
    const [qrCodes, setQrCodes] = useState({});

    useEffect(() => {
        const generateQRCodes = async () => {
            if (!labelData || !Array.isArray(labelData)) return;
            
            const newQrCodes = {};
            for (const label of labelData) {
                if (label && label.menuItemId) {
                    newQrCodes[label.menuItemId] = await generateQRCode(label.menuItemId);
                }
            }
            setQrCodes(newQrCodes);
        };

        generateQRCodes();
    }, [labelData]);
    // Add safety check for labelData
    if (!labelData || !Array.isArray(labelData)) {
        return (
            <Document>
                <Page size="LETTER" style={styles.page}>
                    <View style={styles.labelContainer}>
                        <Text>No label data available</Text>
                    </View>
                </Page>
            </Document>
        );
    }

    // Define renderLabel function before it's used
    const renderLabel = (label, index) => {
        if (!label) {
            return (
                <View key={index} style={styles.labelContainer}>
                    {/* Empty label */}
                </View>
            );
        }

        return (
            <View key={index} style={styles.labelContainer}>
                <Text style={styles.dishText}>{label.dish}</Text>
                <Text style={styles.dayText}>{label.day}</Text>
                <Text style={styles.nameText}>{label.name}</Text>
                <Text style={styles.zoneText}>{label.zone}</Text>
                <Text style={styles.sizeText}>{label.size}</Text>
                {qrCodes[label.menuItemId] && (
                    <Image style={styles.qrCode} src={qrCodes[label.menuItemId]} />
                )}
            </View>
        );
    };

    // Calculate how many pages we need
    const totalLabels = labelData.length;
    const totalPages = Math.ceil(totalLabels / AVERY_5160.labelsPerPage);

    // Create pages
    const pages = [];

    for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
        const startIndex = pageIndex * AVERY_5160.labelsPerPage;
        const endIndex = Math.min(startIndex + AVERY_5160.labelsPerPage, totalLabels);
        const pageLabels = labelData.slice(startIndex, endIndex);

        // Add empty labels to fill the page if needed (only for the last page)
        const paddedPageLabels = [...pageLabels];
        while (paddedPageLabels.length < AVERY_5160.labelsPerPage) {
            paddedPageLabels.push(null);
        }

        // Group labels into rows of 3 for this page
        const labelRows = [];
        for (let i = 0; i < paddedPageLabels.length; i += AVERY_5160.cols) {
            labelRows.push(paddedPageLabels.slice(i, i + AVERY_5160.cols));
        }

        pages.push(
            <Page key={pageIndex} size="LETTER" style={styles.page}>
                {labelRows.map((row, rowIndex) => (
                    <View key={rowIndex} style={styles.labelRow}>
                        {row.map((label, colIndex) => {
                            const globalIndex = startIndex + rowIndex * AVERY_5160.cols + colIndex;
                            return renderLabel(label, globalIndex);
                        })}
                    </View>
                ))}
            </Page>
        );
    }

    return (
        <Document>
            {pages}
        </Document>
    );
};

export default AveryPdfDocument;